import { useState } from "react";
import { Button } from "../../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import { Check, ChevronDown } from "lucide-react";

interface InlineEditableSelectOption {
  value: string;
  label: string;
  description?: string;
}

interface InlineEditableSelectProps {
  value: string | boolean;
  options: readonly InlineEditableSelectOption[];
  onSave: (newValue: string | boolean) => Promise<void>;
  renderDisplay?: (value: string | boolean) => React.ReactNode;
  disabled?: boolean;
  className?: string;
  /** Hide the hover chevron (for triggers whose display already reads as
   * clickable, e.g. a bordered badge). */
  hideChevron?: boolean;
}

export const InlineEditableSelect = ({
  value,
  options,
  onSave,
  renderDisplay,
  disabled = false,
  className = "",
  hideChevron = false,
}: InlineEditableSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSelect = async (newValue: string | boolean) => {
    if (newValue === value) {
      setIsOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(newValue);
      setIsOpen(false);
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const currentOption = options.find((opt) => opt.value === String(value));
  const displayValue = renderDisplay
    ? renderDisplay(value)
    : currentOption?.label || String(value);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          className={`group cursor-pointer inline-flex items-center gap-2 ${className} ${
            disabled ? "cursor-default" : ""
          }`}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          {displayValue}
          {!disabled && !hideChevron && (
            <ChevronDown className="h-3 w-3 text-font-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="space-y-1">
          {options.map((option) => (
            <Button
              key={option.value}
              variant="ghost"
              className="w-full justify-start text-left h-auto py-2 px-3"
              onClick={() => handleSelect(option.value)}
              disabled={isSaving}
            >
              <div className="flex items-start gap-2 w-full">
                <div className="flex-1">
                  <div className="font-medium">{option.label}</div>
                  {option.description && (
                    <div className="text-xs text-font-secondary mt-0.5">
                      {option.description}
                    </div>
                  )}
                </div>
                {String(value) === option.value && (
                  <Check className="h-4 w-4 shrink-0 mt-0.5" />
                )}
              </div>
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
